/*
 * Copyright (C) Serfim TIC
 * All rights reserved
 */
import { Command } from '../../../model/Command';
import { Filter } from '../../../model/Filter';
import { ListController } from '../../ListController';

class EquipmentListController extends ListController {
    /** @ngInject */
    constructor(equipmentService, waitSpinnerService, $q, $state, $stateParams, $log,
        notificationService, configuration, domainService, siteService, companyService, equipmentTemplateService,
        filterService, dialogService, dateTimeService, $scope) {
        super(configuration, notificationService, waitSpinnerService, $q, $state, $stateParams, $log);
        this.equipmentService = equipmentService;
        this.domainService = domainService;
        this.siteService = siteService;
        this.companyService = companyService;
        this.equipmentTemplateService = equipmentTemplateService;
        this.filterService = filterService;
        this.dialogService = dialogService;
        this.dateTimeService = dateTimeService;
        this.$scope = $scope;
        // mdDataTable doesn't support ES6 syntax with $ctrl variable in templates. That's why we must attach the reload
        // callback to the scope with an arrow function so as it can be called by this component with the correct 'this'
        // binding.
        this.$scope.reload = () => this.reload();
        this.$log.debug('Controller \'EquipmentList\' loaded');
    }

    $onInit() {
        super.$onInit();

        this.waitSpinnerService.visible = true;
        this.paging.sort = 'name';
        this.commands = [];
        this.initFilters();

        this.handleRestServiceCall(this.loadFilters(), () => {
            this.initCommands();
            this.reload();
        }).finally(() => {
            this.waitSpinnerService.visible = false;
        });
    }

    /**
     * Initializes the filters available at the top of the view.
     */
    initFilters() {
        this.siteFilter = Filter.equalFilterEmpty('site.id', 'model.site.site', 'SELECT_AUTOCOMPLETE', []);

        this.domainFilter = Filter.equalFilterEmpty('site.domain.id', 'model.domain.domain', 'SELECT', [],
            domainId => this.getFilterSites(domainId).then(() => this.getFilterEquipmentTemplates(domainId)));

        this.companyFilter = Filter.equalFilterEmpty('delegateeCompany.id', 'model.equipment.delegateecompany',
            'SELECT', []);

        this.equipmentTemplateFilter = Filter.equalFilterEmpty('equipmentTemplate.id',
            'model.equipmenttemplate.equipmenttemplate', 'SELECT', []);

        this.filters.push(
            Filter.emptyLikeFilter('name', 'model.equipment.name'),
            Filter.emptyLikeFilter('code', 'model.equipment.code'),
            this.domainFilter,
            this.siteFilter,
            this.companyFilter,
            this.equipmentTemplateFilter,
        );
    }

    /**
     * Initializes the commands available in the view header.
     */
    initCommands() {
        this.commands.push(
            Command.stateCommand('ui.equipment.create', 'images/svg/add.svg', 'gmao.equipment.create',
                ['ADMIN', 'EQUIPMENT_WRITE']),
            Command.handlerCommand(undefined, 'images/svg/reset.svg', () => this.reload())
        );
    }

    /**
     * Loads data in filters.
     */
    loadFilters() {
        return this.getFilterDomains()
            .then(() => this.getFilterSites(this.domainFilter.value))
            .then(() => this.getFilterCompanies())
            .then(() => this.getFilterEquipmentTemplates(this.domainFilter.value));
    }

    /**
     * Gets the list of domains.
     *
     * @return {Object[]} Promise providing an array of domains.
     */
    getFilterDomains() {
        return this.filterService.getFilterOptions(this.domainService, this.domainFilter, 'name');
    }

    /**
     * Gets the list of sites for a given domain.
     *
     * @param {Number} [domainId] Domain ID, may be undefined if no domain is selected.
     * @return {Object[]} Promise providing an array of sites.
     */
    getFilterSites(domainId) {
        return this.filterService.getOrClearFilterOptions(this.siteService, this.siteFilter, 'name',
            [Filter.equalFilter('domain.id', domainId)], domainId);
    }

    /**
     * Gets the list of companies.
     *
     * @return {Object[]} Promise providing an array of companies.
     */
    getFilterCompanies() {
        return this.filterService.getFilterOptions(this.companyService, this.companyFilter);
    }

    /**
     * Gets the list of equipment templates for a given domain.
     *
     * @param {Number} [domainId] Domain ID, may be undefined if no domain is selected.
     * @return {Object[]} Promise providing an array of equipment templates.
     */
    getFilterEquipmentTemplates(domainId) {
        return this.filterService.getOrClearFilterOptions(this.equipmentTemplateService, this.equipmentTemplateFilter,
            'name', [Filter.equalFilter('domain.id', domainId)], domainId);
    }

    /**
     * Reloads the list of equipments.
     *
     * @return {Promise} A promise providing the list of equipments.
     */
    reload() {
        return this.handleRestServiceCall(
            this.equipmentService.searchEntities({
                params: this.filterService.buildPagingParams(this.paging),
                data: this.filterService.buildFilters(this.filters)
            }),
            data => {
                this.model.data = data.content;
                this.model.data.forEach(equipment => {
                    equipment.inServiceDate = this.dateTimeService.parseLocalDate(equipment.inServiceDate);
                    equipment.remainingWarrantyPeriod = this.dateTimeService.getRemainingMonths(equipment.inServiceDate, equipment.warrantyPeriod);
                });
                this.model.totalElements = data.totalElements;
            }
        );
    }

    /**
     * Shows a confirmation dialog before deleting an equipment. If the dialog is confirmed, the is deleted.
     *
     * @param {integer} id Equipment ID.
     */
    showDeleteConfirmationDialog(id) {
        return this.dialogService.showConfirmationDialogWithMessage('ui.equipment.deleteconfirmation').then(
            confirmed => {
                if (confirmed) {
                    this.deleteEquipment(id);
                }
                return confirmed;
            }
        );
    }

    /**
     * Deletes immediately an equipment without confirmation, and reloads the list of equipments.
     *
     * @param {integer} id Equipment ID.
     */
    deleteEquipment(id) {
        return this.handleRestServiceCall(this.equipmentService.deleteEntity(id), () => {
            this.notificationService.pushMessage('ui.equipment.deleted',
                this.configuration.NOTIFIER_LEVEL_INFO);
            this.reload();
        });
    }
}

export const equipmentListComponent = {
    template: require('./equipment-list.html'),
    controller: EquipmentListController
};



// WEBPACK FOOTER //
// ./src/main/front/scripts/module/equipment/component/EquipmentList.js